// components/booking/UnifiedFloatingCart.tsx — Global floating cart bar (Block 2)
// Reads from UnifiedCartContext, shows lodge + activity sections, totals in USD.
// Visible when totalItemCount > 0 AND pathname !== '/checkout'.

'use client'

import { useEffect, useRef, useCallback } from 'react'
import { useRouter, usePathname } from 'next/navigation'
import { useLocale, useTranslations } from 'next-intl'
import { ShoppingCart, ChevronUp, Trash2, Mountain, Sparkles, ArrowLeft, Truck } from 'lucide-react'
import { useUnifiedCart } from '@/lib/unified-cart'
import { useExchangeRate } from '@/lib/hooks/useExchangeRate'
import styles from './UnifiedFloatingCart.module.css'

export default function UnifiedFloatingCart() {
  const router = useRouter()
  const pathname = usePathname()
  const {
    lodgeItems,
    activityItems,
    serviceItems,
    totalItemCount,
    removeItem,
    removeServiceItem,
    isCartOpen,
    setCartOpen,
    grandTotalUSD,
    isHydrated,
  } = useUnifiedCart()

  const { convertCLPtoUSD } = useExchangeRate()
  const bottomSheetRef = useRef<HTMLDivElement>(null)
  const overlayRef = useRef<HTMLDivElement>(null)

  // Drag refs (using refs instead of state for perf during touch)
  const dragStartY = useRef(0)
  const isDragging = useRef(false)
  const currentDragDelta = useRef(0)
  const triggerStartY = useRef(0)
  const scrollYRef = useRef(0)

  const rawLocale = useLocale()
  const locale = (rawLocale === 'es' || rawLocale === 'pt') ? rawLocale : 'en'
  const t = useTranslations('unifiedCart')

  // Hide on /checkout
  const isCheckoutPage = pathname?.startsWith('/checkout') || pathname?.startsWith('/booking/checkout')
  const shouldShow = isHydrated && totalItemCount > 0 && !isCheckoutPage

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr + 'T12:00:00')
    const dateLocales = { en: 'en-US', es: 'es-CL', pt: 'pt-BR' } as const
    return date.toLocaleDateString(dateLocales[locale], { month: 'short', day: 'numeric' })
  }

  const fmtUSD = (amount: number) =>
    `$${amount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`

  // ─── Animated close: slide down + fade overlay ───
  const isClosingRef = useRef(false)
  const closeCart = useCallback(() => {
    if (isClosingRef.current) return
    isClosingRef.current = true
    if (bottomSheetRef.current) {
      bottomSheetRef.current.style.transition = 'transform 0.3s cubic-bezier(0.32, 0.72, 0, 1)'
      bottomSheetRef.current.style.transform = 'translateY(100%)'
    }
    if (overlayRef.current) {
      overlayRef.current.style.transition = 'opacity 0.3s ease'
      overlayRef.current.style.opacity = '0'
    }
    setTimeout(() => {
      setCartOpen(false)
      isClosingRef.current = false
    }, 300)
  }, [setCartOpen])

  const handleCheckout = () => {
    if (totalItemCount === 0) return
    closeCart()
    setTimeout(() => router.push('/checkout'), 320)
  }

  const toggleCart = () => {
    if (isCartOpen) closeCart()
    else setCartOpen(true)
  }

  // ─── Body scroll lock (iOS-safe: position fixed + scroll restore) ───
  // Only apply when the cart is actually visible (shouldShow).
  // addActivityItem/addLodgeItem call setCartOpen(true) automatically, but in
  // checkout (shouldShow=false) the bottom sheet never renders — without this
  // guard the body would stay position:fixed with no way for the user to close
  // the cart and restore scroll.
  useEffect(() => {
    if (isCartOpen && shouldShow) {
      scrollYRef.current = window.scrollY
      document.body.style.overflow = 'hidden'
      document.body.style.position = 'fixed'
      document.body.style.top = `-${scrollYRef.current}px`
      document.body.style.width = '100%'
      return () => {
        const y = scrollYRef.current
        document.body.style.overflow = ''
        document.body.style.position = ''
        document.body.style.top = ''
        document.body.style.width = ''
        // Force instant scroll — bypass any CSS scroll-behavior: smooth
        document.documentElement.style.scrollBehavior = 'auto'
        window.scrollTo(0, y)
        requestAnimationFrame(() => {
          document.documentElement.style.scrollBehavior = ''
        })
      }
    }
  }, [isCartOpen, shouldShow])

  // Close on outside click
  useEffect(() => {
    if (!isCartOpen) return
    const handleClickOutside = (e: MouseEvent) => {
      if (bottomSheetRef.current && !bottomSheetRef.current.contains(e.target as Node)) {
        closeCart()
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [isCartOpen, closeCart])

  // Close on Escape
  useEffect(() => {
    if (!isCartOpen) return
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') closeCart()
    }
    document.addEventListener('keydown', handleEscape)
    return () => document.removeEventListener('keydown', handleEscape)
  }, [isCartOpen, closeCart])

  // ─── Swipe: drag handle/header → close sheet ──────────────────────
  const handleSheetTouchStart = useCallback((e: React.TouchEvent) => {
    dragStartY.current = e.touches[0].clientY
    isDragging.current = true
    currentDragDelta.current = 0
  }, [])

  const handleSheetTouchMove = useCallback((e: React.TouchEvent) => {
    if (!isDragging.current) return
    const delta = e.touches[0].clientY - dragStartY.current
    // Only allow dragging downward
    if (delta > 0) {
      currentDragDelta.current = delta
      if (bottomSheetRef.current) {
        bottomSheetRef.current.style.transform = `translateY(${delta}px)`
        bottomSheetRef.current.style.transition = 'none'
      }
      if (overlayRef.current) {
        overlayRef.current.style.opacity = `${Math.max(0, 1 - delta / 300)}`
        overlayRef.current.style.transition = 'none'
      }
    }
  }, [])

  const handleSheetTouchEnd = useCallback(() => {
    isDragging.current = false
    if (currentDragDelta.current > 100) {
      // Past threshold → close with animation
      if (bottomSheetRef.current) {
        bottomSheetRef.current.style.transition = 'transform 0.25s cubic-bezier(0.32, 0.72, 0, 1)'
        bottomSheetRef.current.style.transform = 'translateY(100%)'
      }
      if (overlayRef.current) {
        overlayRef.current.style.transition = 'opacity 0.25s ease'
        overlayRef.current.style.opacity = '0'
      }
      setTimeout(() => setCartOpen(false), 250)
    } else if (currentDragDelta.current > 0) {
      // Below threshold → snap back
      if (bottomSheetRef.current) {
        bottomSheetRef.current.style.transition = 'transform 0.3s cubic-bezier(0.32, 0.72, 0, 1)'
        bottomSheetRef.current.style.transform = 'translateY(0)'
      }
      if (overlayRef.current) {
        overlayRef.current.style.transition = 'opacity 0.3s ease'
        overlayRef.current.style.opacity = '1'
      }
    }
    currentDragDelta.current = 0
  }, [setCartOpen])

  // ─── Swipe: trigger bar → swipe up to open ──────────────────────
  const handleTriggerTouchStart = useCallback((e: React.TouchEvent) => {
    triggerStartY.current = e.touches[0].clientY
  }, [])

  const handleTriggerTouchEnd = useCallback((e: React.TouchEvent) => {
    const delta = triggerStartY.current - e.changedTouches[0].clientY
    if (delta > 50) {
      e.preventDefault() // Prevent click from also firing
      setCartOpen(true)
    }
  }, [setCartOpen])

  if (!shouldShow) return null

  return (
    <div className={styles.cartWrapper}>
      {/* Trigger Bar */}
      <button
        className={styles.trigger}
        onClick={toggleCart}
        onTouchStart={handleTriggerTouchStart}
        onTouchEnd={handleTriggerTouchEnd}
      >
        <ShoppingCart size={22} strokeWidth={1.75} />
        <div className={styles.triggerInfo}>
          <span className={styles.triggerItems}>
            {t('itemCount', { count: totalItemCount })}
          </span>
          <span className={styles.triggerTotal}>{fmtUSD(grandTotalUSD)} USD</span>
        </div>
        <ChevronUp size={20} strokeWidth={2} className={isCartOpen ? styles.chevronOpen : ''} />
      </button>

      {/* Bottom Sheet */}
      {isCartOpen && (
        <>
          <div className={styles.overlay} ref={overlayRef} onClick={closeCart} />
          <div className={styles.bottomSheet} ref={bottomSheetRef}>
            {/* Drag area — swipe down to close */}
            <div
              className={styles.dragArea}
              onTouchStart={handleSheetTouchStart}
              onTouchMove={handleSheetTouchMove}
              onTouchEnd={handleSheetTouchEnd}
            >
              <div className={styles.sheetHandle} />
              <div className={styles.sheetHeader}>
                <h3 className={styles.sheetTitle}>{t('yourCart')}</h3>
                <button className={styles.continueButton} onClick={closeCart}>
                  <ArrowLeft size={16} strokeWidth={2} />
                  <span>{t('continueShopping')}</span>
                </button>
              </div>
            </div>

            <div className={styles.sheetItems}>
              {/* Lodge Section */}
              {lodgeItems.length > 0 && (
                <>
                  <div className={styles.sectionHeader}>
                    <Mountain size={14} strokeWidth={2} className={styles.sectionIcon} />
                    <span className={styles.sectionTitle}>{t('lodging')}</span>
                  </div>
                  {lodgeItems.map(item => (
                    <div key={item.cartItemId} className={styles.item}>
                      {item.itemImage && (
                        <div className={styles.itemImage} style={{ backgroundImage: `url(${item.itemImage})` }} />
                      )}
                      <div className={styles.itemBody}>
                        <h4 className={styles.itemName}>{item.itemName}</h4>
                        <p className={styles.itemMeta}>
                          {formatDate(item.checkIn)} – {formatDate(item.checkOut)} · {item.nights}n · {t('guestCount', { count: item.guests })}
                        </p>
                      </div>
                      <div className={styles.itemRight}>
                        <span className={styles.itemPrice}>{fmtUSD(convertCLPtoUSD(item.priceCLP))}</span>
                        <button
                          className={styles.removeButton}
                          onClick={() => removeItem(item.cartItemId)}
                          aria-label={t('removeItem')}
                        >
                          <Trash2 size={15} strokeWidth={1.75} />
                        </button>
                      </div>
                    </div>
                  ))}
                </>
              )}

              {/* Activity Section */}
              {activityItems.length > 0 && (
                <>
                  <div className={styles.sectionHeader}>
                    <Sparkles size={14} strokeWidth={2} className={styles.sectionIcon} />
                    <span className={styles.sectionTitle}>{t('activities')}</span>
                  </div>
                  {activityItems.map(item => (
                    <div key={item.cartItemId} className={styles.item}>
                      {item.activityImage && (
                        <div className={styles.itemImage} style={{ backgroundImage: `url(${item.activityImage})` }} />
                      )}
                      <div className={styles.itemBody}>
                        <h4 className={styles.itemName}>{item.activityName}</h4>
                        <p className={styles.itemMeta}>
                          {formatDate(item.date)} · {t('personCount', { count: item.persons || item.adults })}
                        </p>
                      </div>
                      <div className={styles.itemRight}>
                        <span className={styles.itemPrice}>{fmtUSD(item.subtotalUSD)}</span>
                        <button
                          className={styles.removeButton}
                          onClick={() => removeItem(item.cartItemId)}
                          aria-label={t('removeItem')}
                        >
                          <Trash2 size={15} strokeWidth={1.75} />
                        </button>
                      </div>
                    </div>
                  ))}
                </>
              )}

              {/* Services Section */}
              {serviceItems.length > 0 && (
                <>
                  <div className={styles.sectionHeader}>
                    <Truck size={14} strokeWidth={2} className={styles.sectionIcon} />
                    <span className={styles.sectionTitle}>{t('services')}</span>
                  </div>
                  {serviceItems.map(item => {
                    const outbound = item.segments.find(s => s.kind === 'outbound')
                    const ret = item.segments.find(s => s.kind === 'return')
                    const segSummary = [
                      outbound ? `Outbound ${formatDate(outbound.date)} ${outbound.time}` : null,
                      ret ? `Return ${formatDate(ret.date)} ${ret.time}` : null,
                    ].filter(Boolean).join(' + ')
                    return (
                      <div key={item.cartItemId} className={styles.item}>
                        {item.activityImage && (
                          <div className={styles.itemImage} style={{ backgroundImage: `url(${item.activityImage})` }} />
                        )}
                        <div className={styles.itemBody}>
                          <h4 className={styles.itemName}>{item.activityName}</h4>
                          <p className={styles.itemMeta}>
                            {segSummary} · {t('personCount', { count: item.persons })}
                          </p>
                        </div>
                        <div className={styles.itemRight}>
                          <span className={styles.itemPrice}>{fmtUSD(item.subtotalUSD)}</span>
                          <button
                            className={styles.removeButton}
                            onClick={() => removeServiceItem(item.cartItemId)}
                            aria-label={t('removeItem')}
                          >
                            <Trash2 size={15} strokeWidth={1.75} />
                          </button>
                        </div>
                      </div>
                    )
                  })}
                </>
              )}
            </div>

            {/* Footer — total + checkout only */}
            <div className={styles.sheetFooter}>
              <div className={styles.totalRow}>
                <span className={styles.totalLabel}>{t('total')}</span>
                <span className={styles.totalAmount}>{fmtUSD(grandTotalUSD)} USD</span>
              </div>
              <button className={styles.checkoutButton} onClick={handleCheckout}>
                {t('proceedToCheckout')}
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  )
}

